import { Box, useMediaQuery } from '@chakra-ui/react';
import type React from 'react';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { IoIosSettings } from 'react-icons/io';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import Preview from '@/components/Templates/BenefitVerification/Steps/Preview';
import { DenialSupportDetails } from '@/components/Templates/DenialSupport';
import DetailsView from '@/components/Templates/DetailsView';
import { OrderReturnDetails } from '@/components/Templates/Returns';
import { SupportTicketDetails } from '@/components/Templates/SupportTickets';
import { EditForm } from '@/components/Templates/Utilities/EditForm';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import {
	BgProcessModule,
	useGetBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { buildDocumentsUrl } from '@/utils/navigationItems';
import Show from '../Show';
import BgProcessListItem, { type ProcessItem } from './BgProcessListItem';
import { useBgModuleDetails } from './useBgModuleDetails';

type BackgroundProcessProps = {
	userId: string | null;
};
interface ModalState {
	module: BgProcessModule | null;
	mode: 'create' | 'edit' | '' | null;
	element?: React.ReactNode;
	title?: string;
	data?: any;
	size?: 'md' | 'lg' | 'xl' | 'fullwidth';
	step?: number;
	customSx?: Record<string, any>;
}

const baseURL = `${import.meta.env.VITE_BGCRON_URL || 'http://localhost:3011'}`;

const BackgroundProcess: FC<BackgroundProcessProps> = ({ userId }) => {
	const [selectedItem, setSelectedItem] = useState<ProcessItem | null>(null);
	const { defaultClinicIds } = useClinicFilter('_ta_clinic_value');

	const eventSourceRef = useRef<EventSource | null>(null);
	const { data: bgProcesses } = useGetBgProcess({
		clinicIds: defaultClinicIds.join(',') || '',
		enabled: defaultClinicIds.length > 0,
	});
	const { data: moduleData, isLoading } = useBgModuleDetails(
		selectedItem?.moduleName as BgProcessModule,
		selectedItem,
	);

	const { hasPermission } = usePermission();

	const nodeRef = useRef(null);
	const ICON_SIZE = 40; // draggable icon width
	const INITIAL_Y = 354;
	const INITIAL_RIGHT = 20;
	const DIALOG_WIDTH = 400;

	const [modalState, setModalState] = useState<ModalState>({
		module: null,
		mode: null,
	});
	const [position, setPosition] = useState({ x: 0, y: INITIAL_Y });
	const [startPosition, setStartPosition] = useState({ x: 0, y: INITIAL_Y });
	const [isDragging, setIsDragging] = useState(false);
	const [open, setOpen] = useState(false);
  const [isBelow930] = useMediaQuery(['(max-width: 930px)']);

	const hiddenRoutes = ['/login', '/choose-platform'];
	const isVisible = window?.location
		? Boolean(userId) && !hiddenRoutes.includes(window.location.pathname)
		: Boolean(userId);

	const boxShadow = useColorModeValue(
		'0 0 15px 5px rgba(17, 121, 100, 0.4)',
		'0 0 15px 5px rgba(17, 121, 100, 0.4)',
	);

	// Clamp a value between min and max
	const clamp = (value: number, min: number, max: number) =>
		Math.min(Math.max(value, min), max);

	// Set initial draggable position
	useEffect(() => {
		const setInitialPosition = () => {
			const initialX = window.innerWidth - INITIAL_RIGHT - ICON_SIZE;
			const initialY = INITIAL_Y;

			setPosition({ x: initialX, y: initialY });
			setStartPosition({ x: initialX, y: initialY });
		};

		setInitialPosition();
		window.addEventListener('resize', setInitialPosition);
		return () => window.removeEventListener('resize', setInitialPosition);
	}, []);

	// Streaming and getting latest data from db
	useEffect(() => {
		if (!userId) return;

		if (eventSourceRef.current) return; // already connected

		const connect = () => {
			const es = new EventSource(`${baseURL}/bg-process/stream/${userId}`, {
				withCredentials: false,
			});
			eventSourceRef.current = es;

			es.onmessage = (event) => {
				const data = JSON.parse(event.data);
				if (data.userId !== userId) return;

				if (data.status === 'running') {
					setOpen(true);
				}
			};

			es.onerror = (err) => {
				console.error('SSE Error:', err);
				es.close();
				eventSourceRef.current = null;
				setTimeout(connect, 5000); // reconnect
			};
		};

		connect();

		return () => {
			eventSourceRef.current?.close();
			eventSourceRef.current = null;
		};
	}, [userId]);

	//This will be used to open respective module pop for displaying the details
	const handleModuleRedirection = (item: ProcessItem) => {
		setSelectedItem(item);

		switch (item.moduleName) {
			case BgProcessModule.BV: {
				setModalState({
					module: BgProcessModule.BV,
					title: 'Benefits Verification Details',
					mode: 'edit',
					size: 'xl',
					data: item.response,
					customSx: {
						width: '90vw',
						maxWidth: '95vw',
						maxHeight: '90vh',
						overflowY: 'auto',
					},
				});
				break;
			}
			case BgProcessModule.ORDER_REQUEST_SINGLE: {
				setModalState({
					module: BgProcessModule.ORDER_REQUEST_SINGLE,
					title: 'Single Order Details',
					mode: 'edit',
					size: 'xl',
					data: item?.response?.body,
				});
				break;
			}
			case BgProcessModule.ORDER_REQUEST_BULK: {
				setModalState({
					module: BgProcessModule.ORDER_REQUEST_BULK,
					title: 'Bulk Order Details',
					mode: 'edit',
					size: 'xl',
					data: (item?.response?.body?.results || [])?.map(
						(item: any) => item?.data,
					),
				});
				break;
			}
			case BgProcessModule.RETURN_REQUEST: {
				setModalState({
					module: BgProcessModule.RETURN_REQUEST,
					title: 'Details',
					mode: 'edit',
					size: 'xl',
					data: item?.response?.body?.items || [],
					element: (
						<OrderReturnDetails
							returnOrderId={(item?.response?.body?.returnOrderIds || [])[0]}
						/>
					),
				});
				break;
			}
			case BgProcessModule.CONSIGNMENT_ORDER: {
				setModalState({
					module: BgProcessModule.CONSIGNMENT_ORDER,
					title: 'Consignment Order Details',
					mode: 'edit',
					size: 'xl',
					data: (item?.response?.body?.results || [])?.map(
						(item: any) => item?.data,
					),
				});
				break;
			}
			case BgProcessModule.PATIENTS: {
				setModalState({
					module: BgProcessModule.PATIENTS,
					title: 'Patient Details',
					mode: 'edit',
					size: 'xl',
					data: item?.response?.body,
				});
				break;
			}
			case BgProcessModule.UTILITIES_ASSIGN_GRAFTS: {
				setModalState({
					module: BgProcessModule.UTILITIES_ASSIGN_GRAFTS,
					title: 'Assign Graft Details',
					mode: 'edit',
					size: 'xl',
					data: item?.response?.body?.items
						? item?.response?.body?.items[0]
						: null,
					element: (
						<EditForm
							mode="details"
							initialData={
								item?.response?.body?.items
									? item?.response?.body?.items[0]
									: null
							}
							onSuccess={() => {
								setModalState({ mode: null, module: null });
							}}
						/>
					),
				});
				break;
			}
			case BgProcessModule.UTILITIES_CONFIRM_GRAFTS: {
				setModalState({
					module: BgProcessModule.UTILITIES_CONFIRM_GRAFTS,
					title: 'Confirm Graft Details',
					mode: 'edit',
					size: 'xl',
					data: item?.response?.body?.items,
					element: (
						<EditForm
							type="confirm"
							mode="details"
							initialData={item?.response?.body?.items}
							onSuccess={() => {
								setModalState({ mode: null, module: null });
							}}
						/>
					),
				});
				break;
			}
			case BgProcessModule.SUPPORT_TICKET: {
				setModalState({
					module: BgProcessModule.SUPPORT_TICKET,
					title: 'Support Details',
					mode: 'edit',
					size: 'lg',
					data: item.response?.body,
				});
				break;
			}
		case BgProcessModule.DENIAL_SUPPORT: {
			setModalState({
				module: BgProcessModule.DENIAL_SUPPORT,
				title: 'Denial Support Details',
				mode: 'edit',
				size: 'md',
				data: item.response?.body,
			});
			break;
		}
		}
	};

	const renderModuleContent = () => {
		if (isLoading)
			return (
				<FaceliftDataLoader
					title="Loading Details"
					description="Hang tight while we fetch the details for you."
				/>
			);

		switch (modalState.module) {
			case BgProcessModule.BV:
				return (
					<Preview
						bvInformation={moduleData}
						onNavigateToDoc={() => {
							// Close both modals and navigate to documents
							const url = String(moduleData?.portalBVUploadUrl) || '';
							if (url && moduleData?.clinic) {
								const documentsUrl = buildDocumentsUrl(
									url,
									moduleData.clinic,
									moduleData.clinicName,
								);
								if (documentsUrl) {
									// Close the BV details modal
									setModalState({ mode: null, module: null });
									// Close the BGProcess list modal
									setOpen(false);
									// Navigate to documents page
									window.location.href = documentsUrl;
								}
							}
						}}
					/>
				);
			case BgProcessModule.ORDER_REQUEST_SINGLE:
				return (
					<DetailsView
						data={moduleData}
						isLoading={false}
						gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					/>
				);
			case BgProcessModule.ORDER_REQUEST_BULK:
				return (
					<DetailsView
						data={moduleData}
						isLoading={false}
						gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					/>
				);
			case BgProcessModule.CONSIGNMENT_ORDER:
				return (
					<DetailsView
						data={moduleData}
						isLoading={false}
						gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					/>
				);
			case BgProcessModule.PATIENTS:
				return <DetailsView data={moduleData} isLoading={false} />;
			case BgProcessModule.SUPPORT_TICKET:
				return <SupportTicketDetails data={moduleData} isLoading={false} />;
			case BgProcessModule.DENIAL_SUPPORT:
				return <DenialSupportDetails data={moduleData} isLoading={false} />;
		}
	};

	return (
		<Show when={isVisible}>
			<Draggable
				nodeRef={nodeRef}
				axis="both"
				bounds={{
					left: 0,
					top: 10,
					right: window.innerWidth - ICON_SIZE,
					bottom: window.innerHeight - 100,
				}}
				position={position}
				onStart={(e, data) => {
					//This will block text selection while dragging
					document.body.style.userSelect = 'none';
					document.body.style.webkitUserSelect = 'none';
					setStartPosition({ x: data.x, y: data.y });
					setIsDragging(false);
				}}
				onDrag={() => setIsDragging(true)}
				onStop={(e, data) => {
					//Re-enable text selection after dragging
					document.body.style.userSelect = 'auto';
					document.body.style.webkitUserSelect = 'auto';

					const withinY = data.y > 10 && data.y < window.innerHeight - 80;
					if (withinY) {
						// Snap to left or right edges
						if (data.x > window.innerWidth / 2) {
							setPosition({ x: window.innerWidth - ICON_SIZE, y: data.y });
						} else {
							setPosition({ x: 0, y: data.y });
						}
					} else {
						setPosition(startPosition);
					}
				}}
			>
				<Box
					ref={nodeRef}
					position="absolute"
					zIndex={9999}
					cursor="grab"
					onClick={() => {
						if (!isDragging) setOpen(true);
					}}
					borderRadius="full"
					bgColor="#117964"
					p={1}
				>
					<IoIosSettings size={30} color="white" />
					<Show
						when={
							(bgProcesses || []).filter(
								(item: ProcessItem) =>
									item.status === 'queued' || item.status === 'running',
							).length > 0
						}
					>
						<Box
							position="absolute"
							top="-5px"
							right="0px"
							minWidth="18px"
							height="18px"
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontSize="10px"
							fontWeight="bold"
							backgroundColor="#E1BA1C"
							borderRadius="50%"
							border="2px solid #E1BA1C"
							px={0.5}
						>
							{
								(bgProcesses || []).filter(
									(item: ProcessItem) =>
										item.status === 'queued' || item.status === 'running',
								).length
							}
						</Box>
					</Show>
				</Box>
			</Draggable>

			<FaceliftDialog
				open={open}
				onOpenChange={(open) => {
					if (!open) setOpen(false);
				}}
				sx={{
					maxWidth: `${DIALOG_WIDTH}px`,
					maxHeight: '90vh',
					boxShadow: boxShadow,
					position: 'absolute',
					top: `${position.y - window.scrollY}px`,
					left: `${clamp(
						position.x > window.innerWidth / 2
							? position.x - DIALOG_WIDTH // icon on right → dialog left of icon
							: position.x + ICON_SIZE / 2 + 10, // icon on left → add 10px gap
						0,
						window.innerWidth - DIALOG_WIDTH,
					)}px`,
					borderRadius: '12px',
					backdropFilter: 'blur(10px)',
					transition: 'top 0.2s ease, left 0.2s ease',
				}}
				childrenSx={{
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 8,
				}}
			>
				<BgProcessListItem
					moduleRedirection={handleModuleRedirection}
					processes={(bgProcesses || []).filter((item: any) =>
						hasPermission(item?.permission ? [item?.permission] : []),
					)}
					handleClose={() => setOpen(false)}
				/>
			</FaceliftDialog>

			{/* Bg Module Redirection Popup */}
			<FaceliftDialog
				showBackBtn
				title={modalState?.title}
				open={!!modalState?.module}
				onOpenChange={(open) => {
					if (!open) setModalState({ mode: null, module: null });
				}}
				size={modalState?.size || 'xl'}
				sx={{
					boxShadow: boxShadow,
					backdropFilter: 'blur(10px)',
					...(modalState?.customSx || {}),
				}}
				contentProps={isBelow930 && modalState?.module !== BgProcessModule.BV ? {
					mx: { base: 3, md: 'auto' },
					maxW: { base: '95vw', sm: '80vw', md: '83vw', lg: '70vw' },
				} : {}}
			>
				{renderModuleContent()}
				{modalState?.element}
			</FaceliftDialog>
		</Show>
	);
};

export default BackgroundProcess;
